import React, { useState, useEffect, useRef } from 'react';
import {
    Syringe,
    Plus,
    Calendar,
    Clock,
    User,
    Search,
    X,
    Check,
    AlertCircle,
    ChevronLeft,
    ChevronRight,
    FlaskConical,
    Trash2,
    Edit,
    RefreshCw,
    Phone,
    CalendarClock
} from 'lucide-react';
import './SampleAppointments.css';
import { API_URL } from '../config/api';

const SampleAppointments = () => {
    const getLocalDateYMD = (date = new Date()) => {
        const y = date.getFullYear();
        const m = String(date.getMonth() + 1).padStart(2, '0');
        const d = String(date.getDate()).padStart(2, '0');
        return `${y}-${m}-${d}`;
    };
    const [appointments, setAppointments] = useState([]);
    const [loading, setLoading] = useState(false);
    const [showModal, setShowModal] = useState(false);
    const [selectedDate, setSelectedDate] = useState(getLocalDateYMD());
    const [viewMode, setViewMode] = useState('list'); // 'list' or 'calendar'
    const [calendarView, setCalendarView] = useState(() => {
        return localStorage.getItem('sampleAppointmentsView') || 'day';
    });
    const [stats, setStats] = useState(null);

    // Search states
    const [patientSearch, setPatientSearch] = useState('');
    const [patients, setPatients] = useState([]);
    const [showPatientDropdown, setShowPatientDropdown] = useState(false);
    const [studies, setStudies] = useState([]);

    // Form state
    const [formData, setFormData] = useState({
        patient_id: '',
        patient_name: '',
        appointment_date: getLocalDateYMD(),
        appointment_time: '09:00',
        end_time: '09:30',
        study_ids: [],
        notes: ''
    });

    const [editingId, setEditingId] = useState(null);
    const patientSearchTimerRef = useRef(null);
    const [submittingAppointment, setSubmittingAppointment] = useState(false);
    const [reschedulingAppointment, setReschedulingAppointment] = useState(false);

    // Reschedule states
    const [showRescheduleModal, setShowRescheduleModal] = useState(false);
    const [selectedAppointment, setSelectedAppointment] = useState(null);
    const [rescheduleData, setRescheduleData] = useState({
        appointment_date: '',
        appointment_time: ''
    });

    useEffect(() => {
        fetchAppointments();
        fetchStudies();
        fetchStats();
    }, [selectedDate, calendarView]);

    useEffect(() => {
        return () => {
            if (patientSearchTimerRef.current) {
                clearTimeout(patientSearchTimerRef.current);
            }
        };
    }, []);

    const fetchAppointments = async () => {
        setLoading(true);
        try {
            const token = localStorage.getItem('token');
            const { dateFrom, dateTo } = getDateRange();
            const response = await fetch(`${API_URL}/sample-appointments?date_from=${dateFrom}&date_to=${dateTo}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setAppointments(data.data || []);
            }
        } catch (err) {
            console.error('Error fetching appointments:', err);
        } finally {
            setLoading(false);
        }
    };

    // Calculate date range based on calendar view
    const getDateRange = () => {
        const selected = new Date(selectedDate + 'T12:00:00');
        let dateFrom, dateTo;

        if (calendarView === 'day') {
            dateFrom = selectedDate;
            dateTo = selectedDate;
        } else if (calendarView === 'week') {
            const dayOfWeek = selected.getDay();
            const monday = new Date(selected);
            monday.setDate(selected.getDate() - (dayOfWeek === 0 ? 6 : dayOfWeek - 1));
            const sunday = new Date(monday);
            sunday.setDate(monday.getDate() + 6);
            dateFrom = getLocalDateYMD(monday);
            dateTo = getLocalDateYMD(sunday);
        } else { // month
            const firstDay = new Date(selected.getFullYear(), selected.getMonth(), 1);
            const lastDay = new Date(selected.getFullYear(), selected.getMonth() + 1, 0);
            dateFrom = getLocalDateYMD(firstDay);
            dateTo = getLocalDateYMD(lastDay);
        }

        return { dateFrom, dateTo };
    };

    const fetchStats = async () => {
        try {
            const token = localStorage.getItem('token');
            const { dateFrom, dateTo } = getDateRange();
            const response = await fetch(`${API_URL}/sample-appointments/stats?date_from=${dateFrom}&date_to=${dateTo}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setStats(data.data);
            }
        } catch (err) {
            console.error('Error fetching stats:', err);
        }
    };

    const fetchStudies = async () => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/catalog-studies?tipo_item=estudio`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setStudies(Array.isArray(data) ? data : (data.data || []));
            }
        } catch (err) {
            console.error('Error fetching studies:', err);
        }
    };

    const searchPatients = async (term) => {
        if (term.length < 2) {
            setPatients([]);
            setShowPatientDropdown(false);
            return;
        }
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/patients?search=${encodeURIComponent(term)}`, {
                headers: { 'Authorization': `Bearer ${token}` }
            });
            if (response.ok) {
                const data = await response.json();
                setPatients(Array.isArray(data) ? data : (data.data || data.patients || []));
                setShowPatientDropdown(true);
            }
        } catch (err) {
            console.error('Error searching patients:', err);
        }
    };

    const selectPatient = (patient) => {
        setFormData(prev => ({
            ...prev,
            patient_id: patient.id,
            patient_name: `${patient.first_name} ${patient.last_name}${patient.maternal_last_name ? ' ' + patient.maternal_last_name : ''}`
        }));
        setPatientSearch(`${patient.first_name} ${patient.last_name}${patient.maternal_last_name ? ' ' + patient.maternal_last_name : ''}`);
        setShowPatientDropdown(false);
    };

    const toggleStudy = (studyId) => {
        setFormData(prev => {
            const studyIds = prev.study_ids.includes(studyId)
                ? prev.study_ids.filter(id => id !== studyId)
                : [...prev.study_ids, studyId];
            return { ...prev, study_ids: studyIds };
        });
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        if (submittingAppointment) return;

        if (!formData.patient_id) {
            alert('Por favor selecciona un paciente');
            return;
        }

        if (formData.study_ids.length === 0) {
            alert('Por favor selecciona al menos un estudio');
            return;
        }

        try {
            setSubmittingAppointment(true);
            const token = localStorage.getItem('token');
            const url = editingId
                ? `${API_URL}/sample-appointments/${editingId}`
                : `${API_URL}/sample-appointments`;

            const response = await fetch(url, {
                method: editingId ? 'PUT' : 'POST',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(formData)
            });

            const data = await response.json();

            if (response.ok) {
                alert(editingId ? 'Cita actualizada' : 'Cita creada exitosamente');
                resetForm();
                setShowModal(false);
                fetchAppointments();
                fetchStats();
            } else {
                alert('Error: ' + (data.message || 'No se pudo guardar la cita'));
            }
        } catch (err) {
            console.error('Error saving appointment:', err);
            alert('Error al guardar la cita');
        } finally {
            setSubmittingAppointment(false);
        }
    };

    // Submit reschedule
    const submitReschedule = async (e) => {
        e.preventDefault();
        if (reschedulingAppointment) return;
        if (!selectedAppointment) return;

        try {
            setReschedulingAppointment(true);
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/sample-appointments/${selectedAppointment.id}`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify(rescheduleData)
            });

            if (response.ok) {
                alert('Cita reprogramada exitosamente');
                setShowRescheduleModal(false);
                setSelectedAppointment(null);
                fetchAppointments();
            } else {
                const data = await response.json();
                alert('Error: ' + (data.message || 'No se pudo reprogramar'));
            }
        } catch (err) {
            console.error('Error rescheduling:', err);
            alert('Error de conexión');
        } finally {
            setReschedulingAppointment(false);
        }
    };

    const cancelAppointment = async (id) => {
        if (!window.confirm('¿Estás seguro de cancelar esta cita?')) return;

        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/sample-appointments/${id}`, {
                method: 'DELETE',
                headers: { 'Authorization': `Bearer ${token}` }
            });

            if (response.ok) {
                alert('Cita cancelada');
                fetchAppointments();
                fetchStats();
            }
        } catch (err) {
            console.error('Error canceling appointment:', err);
        }
    };

    const updateStatus = async (id, status) => {
        try {
            const token = localStorage.getItem('token');
            const response = await fetch(`${API_URL}/sample-appointments/${id}/status`, {
                method: 'PUT',
                headers: {
                    'Authorization': `Bearer ${token}`,
                    'Content-Type': 'application/json'
                },
                body: JSON.stringify({ status })
            });

            if (response.ok) {
                fetchAppointments();
                fetchStats();
            }
        } catch (err) {
            console.error('Error updating status:', err);
        }
    };

    const resetForm = () => {
        setFormData({
            patient_id: '',
            patient_name: '',
            appointment_date: selectedDate,
            appointment_time: '09:00',
            end_time: '09:30',
            study_ids: [],
            notes: ''
        });
        setPatientSearch('');
        setEditingId(null);
    };


    const navigateDate = (direction) => {
        const date = new Date(selectedDate + 'T12:00:00');

        if (calendarView === 'day') {
            date.setDate(date.getDate() + direction);
        } else if (calendarView === 'week') {
            date.setDate(date.getDate() + (direction * 7));
        } else { // month
            date.setMonth(date.getMonth() + direction);
        }

        setSelectedDate(getLocalDateYMD(date));
    };

    const goToToday = () => {
        setSelectedDate(getLocalDateYMD());
    };

    // Get label for current date range
    const getDateRangeLabel = () => {
        const selected = new Date(selectedDate + 'T12:00:00');

        if (calendarView === 'day') {
            return selected.toLocaleDateString('es-MX', {
                weekday: 'long',
                day: 'numeric',
                month: 'long',
                year: 'numeric'
            });
        } else if (calendarView === 'week') {
            const { dateFrom, dateTo } = getDateRange();
            const from = new Date(dateFrom + 'T12:00:00');
            const to = new Date(dateTo + 'T12:00:00');
            return `${from.getDate()} - ${to.getDate()} ${to.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' })}`;
        } else { // month
            return selected.toLocaleDateString('es-MX', { month: 'long', year: 'numeric' });
        }
    };

    const formatTime = (time) => {
        if (!time) return '';
        const [hours, minutes] = time.split(':');
        const h = parseInt(hours);
        const displayHour = h === 0 ? 12 : h > 12 ? h - 12 : h;
        return `${displayHour}:${minutes} ${h >= 12 ? 'PM' : 'AM'}`;
    };

    const toCount = (value) => {
        const parsed = Number(value);
        return Number.isFinite(parsed) ? parsed : 0;
    };

    const getStatusBadge = (status) => {
        const statusConfig = {
            scheduled: { label: 'Programada', className: 'status-scheduled' },
            confirmed: { label: 'Confirmada', className: 'status-confirmed' },
            checked_in: { label: 'En Muestras', className: 'status-in-progress' },
            in_progress: { label: 'En Proceso', className: 'status-in-progress' },
            completed: { label: 'Completada', className: 'status-completed' },
            cancelled: { label: 'Cancelada', className: 'status-cancelled' }
        };
        const config = statusConfig[status] || statusConfig.scheduled;
        return <span className={`status-badge ${config.className}`}>{config.label}</span>;
    };

    return (
        <div className="sample-appointments-container">
            {/* Header */}
            <div className="sa-header">
                <div className="sa-title">
                    <Syringe size={28} />
                    <h2>Citas para Toma de Muestras</h2>
                </div>
                <div className="sa-actions">
                    <button className="btn-refresh" onClick={fetchAppointments} disabled={loading}>
                        <RefreshCw size={18} className={loading ? 'spinning' : ''} />
                    </button>
                </div>
            </div>

            {/* Stats Cards */}
            {stats && (
                <div className="sa-stats">
                    <div className="stat-card">
                        <span className="stat-number">{toCount(stats.scheduled)}</span>
                        <span className="stat-label">Programadas</span>
                    </div>
                    <div className="stat-card confirmed">
                        <span className="stat-number">{toCount(stats.confirmed)}</span>
                        <span className="stat-label">Confirmadas</span>
                    </div>
                    <div className="stat-card in-progress">
                        <span className="stat-number">{toCount(stats.in_progress) + toCount(stats.checked_in)}</span>
                        <span className="stat-label">En Proceso</span>
                    </div>
                    <div className="stat-card completed">
                        <span className="stat-number">{toCount(stats.completed)}</span>
                        <span className="stat-label">Completadas</span>
                    </div>
                </div>
            )}

            {/* Date Navigation Toolbar */}
            <div className="sa-toolbar">
                <div className="view-selector">
                    <button
                        className={calendarView === 'day' ? 'active' : ''}
                        onClick={() => {
                            setCalendarView('day');
                            localStorage.setItem('sampleAppointmentsView', 'day');
                        }}
                    >Día</button>
                    <button
                        className={calendarView === 'week' ? 'active' : ''}
                        onClick={() => {
                            setCalendarView('week');
                            localStorage.setItem('sampleAppointmentsView', 'week');
                        }}
                    >Semana</button>
                    <button
                        className={calendarView === 'month' ? 'active' : ''}
                        onClick={() => {
                            setCalendarView('month');
                            localStorage.setItem('sampleAppointmentsView', 'month');
                        }}
                    >Mes</button>
                </div>

                <div className="date-controls">
                    <button className="nav-btn" onClick={() => navigateDate(-1)}><ChevronLeft size={20} /></button>
                    <span className="current-date">{getDateRangeLabel()}</span>
                    <button className="nav-btn" onClick={() => navigateDate(1)}><ChevronRight size={20} /></button>
                    <button className="btn-today" onClick={goToToday}>Hoy</button>
                </div>
            </div>

            {/* Appointments Table */}
            <div className="sa-table-wrapper">
                {loading ? (
                    <div className="loading-message">Cargando citas...</div>
                ) : appointments.length === 0 ? (
                    <div className="empty-message">
                        <Calendar size={48} />
                        <p>No hay citas programadas para {calendarView === 'day' ? 'este día' : calendarView === 'week' ? 'esta semana' : 'este mes'}</p>
                        <span className="empty-hint">Las citas se crean desde Alta de Estudio</span>
                    </div>
                ) : (
                    <table className="sa-table">
                        <thead>
                            <tr>
                                <th>Hora / Fecha</th>
                                <th>Paciente</th>
                                <th>Estudios</th>
                                <th>Contacto</th>
                                <th>Estatus</th>
                                <th style={{ textAlign: 'center' }}>Acciones</th>
                            </tr>
                        </thead>
                        <tbody>
                            {appointments.map(apt => (
                                <tr key={apt.id}>
                                    {/* Hora / Fecha */}
                                    <td>
                                        <div className="col-time">
                                            <Clock size={16} />
                                            <span>{formatTime(apt.appointment_time)}</span>
                                        </div>
                                        {calendarView !== 'day' && (
                                            <span className="col-date-sub">
                                                {new Date(apt.appointment_date + 'T12:00:00').toLocaleDateString('es-MX', { weekday: 'short', day: 'numeric', month: 'short' })}
                                            </span>
                                        )}
                                    </td>

                                    {/* Paciente */}
                                    <td>
                                        <div className="patient-info">
                                            <div className="patient-avatar">
                                                {apt.first_name.charAt(0)}{apt.last_name.charAt(0)}
                                            </div>
                                            <div className="col-patient">
                                                <span>{apt.first_name} {apt.last_name} {apt.maternal_last_name || ''}</span>
                                            </div>
                                        </div>
                                    </td>

                                    {/* Estudios */}
                                    <td className="col-studies">
                                        <div className="study-info-cell">
                                            {apt.study_names ? (
                                                <div className="study-names-list">
                                                    {apt.study_names.split(',').map((study, idx) => (
                                                        <div key={idx} className="study-item-name">{study.trim()}</div>
                                                    ))}
                                                </div>
                                            ) : (
                                                <div className="text-gray-400">-</div>
                                            )}
                                            {apt.notes && !apt.notes.toLowerCase().startsWith('estudio:') && (
                                                <div className="appointment-note-inline">
                                                    {apt.notes}
                                                </div>
                                            )}
                                        </div>
                                    </td>

                                    {/* Contacto */}
                                    <td className="col-contact">
                                        {apt.phone ? (
                                            <div className="contact-item">
                                                <Phone size={14} /> {apt.phone}
                                            </div>
                                        ) : '-'}
                                    </td>

                                    {/* Estatus */}
                                    <td>
                                        {getStatusBadge(apt.status)}
                                    </td>

                                    {/* Acciones */}
                                    <td className="col-actions-cell">
                                        <div className="action-buttons">
                                            {String(apt.status).toLowerCase() === 'scheduled' && (
                                                <>
                                                    <button
                                                        onClick={() => updateStatus(apt.id, 'confirmed')}
                                                        className="btn-action-text btn-confirm"
                                                    >
                                                        <Check size={16} />
                                                        Confirmar
                                                    </button>
                                                    <button
                                                        className="btn-action btn-reschedule"
                                                        onClick={() => {
                                                            setSelectedAppointment(apt);
                                                            setRescheduleData({
                                                                appointment_date: apt.appointment_date,
                                                                appointment_time: apt.appointment_time
                                                            });
                                                            setShowRescheduleModal(true);
                                                        }}
                                                        title="Reprogramar"
                                                    >
                                                        <CalendarClock size={18} />
                                                    </button>
                                                    <button
                                                        className="btn-action btn-cancel"
                                                        onClick={() => cancelAppointment(apt.id)}
                                                        title="Cancelar cita"
                                                    >
                                                        <Trash2 size={18} />
                                                    </button>
                                                </>
                                            )}
                                            {String(apt.status).toLowerCase() === 'confirmed' && (
                                                <>
                                                    <button
                                                        onClick={() => updateStatus(apt.id, 'checked_in')}
                                                        className="btn-action-text btn-arrival"
                                                        title="El paciente está aquí"
                                                    >
                                                        <User size={16} />
                                                        Paciente llego³
                                                    </button>
                                                    <button
                                                        className="btn-action btn-reschedule"
                                                        onClick={() => {
                                                            setSelectedAppointment(apt);
                                                            setRescheduleData({
                                                                appointment_date: apt.appointment_date,
                                                                appointment_time: apt.appointment_time
                                                            });
                                                            setShowRescheduleModal(true);
                                                        }}
                                                        title="Reprogramar"
                                                    >
                                                        <CalendarClock size={18} />
                                                    </button>
                                                    <button
                                                        className="btn-action btn-cancel"
                                                        onClick={() => cancelAppointment(apt.id)}
                                                        title="Cancelar cita"
                                                    >
                                                        <Trash2 size={18} />
                                                    </button>
                                                </>
                                            )}
                                            {(String(apt.status).toLowerCase() === 'checked_in' || String(apt.status).toLowerCase() === 'in_progress') && (
                                                <span className="state-badge queue">
                                                    <Syringe size={14} />
                                                    En cola de enfermería
                                                </span>
                                            )}
                                            {String(apt.status).toLowerCase() === 'completed' && (
                                                <span className="state-badge taken">
                                                    <Check size={14} />
                                                    Muestra tomada
                                                </span>
                                            )}
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                )}
            </div>

            {/* Modal for New/Edit Appointment */}
            {showModal && (
                <div className="modal-overlay" onClick={() => setShowModal(false)}>
                    <div className="modal-content" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>{editingId ? 'Editar Cita' : 'Nueva Cita para Toma de Muestra'}</h3>
                            <button className="modal-close" onClick={() => setShowModal(false)}>
                                <X size={20} />
                            </button>
                        </div>

                        <form onSubmit={handleSubmit} className="modal-form">
                            {/* Patient Search */}
                            <div className="form-group">
                                <label><User size={16} /> Paciente *</label>
                                <div className="patient-search-wrapper">
                                    <input
                                        type="text"
                                        placeholder="Buscar paciente..."
                                        value={patientSearch}
                                        onChange={(e) => {
                                            const value = e.target.value;
                                            setPatientSearch(value);
                                            if (patientSearchTimerRef.current) { clearTimeout(patientSearchTimerRef.current); }
                                            patientSearchTimerRef.current = setTimeout(() => {
                                                searchPatients(value);
                                            }, 300);
                                        }}
                                    />
                                    {showPatientDropdown && patients.length > 0 && (
                                        <div className="patient-dropdown">
                                            {patients.map(p => (
                                                <div
                                                    key={p.id}
                                                    className="patient-option"
                                                    onClick={() => selectPatient(p)}
                                                >
                                                    <strong>{p.first_name} {p.last_name} {p.maternal_last_name || ''}</strong>
                                                    <span>{p.email || p.phone}</span>
                                                </div>
                                            ))}
                                        </div>
                                    )}
                                </div>
                            </div>

                            {/* Date and Time */}
                            <div className="form-row">
                                <div className="form-group">
                                    <label><Calendar size={16} /> Fecha *</label>
                                    <input
                                        type="date"
                                        value={formData.appointment_date}
                                        onChange={(e) => setFormData(prev => ({ ...prev, appointment_date: e.target.value }))}
                                        required
                                    />
                                </div>
                                <div className="form-group">
                                    <label><Clock size={16} /> Hora *</label>
                                    <input
                                        type="time"
                                        value={formData.appointment_time}
                                        onChange={(e) => setFormData(prev => ({ ...prev, appointment_time: e.target.value }))}
                                        required
                                    />
                                </div>
                            </div>

                            {/* Studies Selection */}
                            <div className="form-group">
                                <label><FlaskConical size={16} /> Estudios *</label>
                                <div className="studies-grid">
                                    {studies.map(study => (
                                        <div
                                            key={study.id}
                                            className={`study-chip ${formData.study_ids.includes(study.id) ? 'selected' : ''}`}
                                            onClick={() => toggleStudy(study.id)}
                                        >
                                            {formData.study_ids.includes(study.id) && <Check size={14} />}
                                            <span>{study.name}</span>
                                        </div>
                                    ))}
                                </div>
                                {formData.study_ids.length > 0 && (
                                    <div className="selected-count">
                                        {formData.study_ids.length} estudio(s) seleccionado(s)
                                    </div>
                                )}
                            </div>

                            {/* Notes */}
                            <div className="form-group">
                                <label>Notas</label>
                                <textarea
                                    value={formData.notes}
                                    onChange={(e) => setFormData(prev => ({ ...prev, notes: e.target.value }))}
                                    placeholder="Indicaciones especiales, ayuno, etc."
                                    rows={3}
                                />
                            </div>

                            <div className="modal-actions">
                                <button type="button" className="btn-cancel" onClick={() => setShowModal(false)}>
                                    Cancelar
                                </button>
                                <button type="submit" className="btn-submit" disabled={submittingAppointment}>
                                    {submittingAppointment ? 'Guardando...' : (editingId ? 'Actualizar' : 'Crear Cita')}
                                </button>
                            </div>
                        </form>
                    </div>
                </div>
            )}

            {/* Reschedule Modal */}
            {showRescheduleModal && selectedAppointment && (
                <div className="modal-overlay" onClick={() => setShowRescheduleModal(false)}>
                    <div className="modal-content reschedule-modal" onClick={e => e.stopPropagation()}>
                        <div className="modal-header">
                            <h3>Reprogramar Cita</h3>
                            <button className="modal-close" onClick={() => setShowRescheduleModal(false)}>
                                <X size={20} />
                            </button>
                        </div>

                        <div className="modal-body">
                            <p className="reschedule-patient-name">
                                Paciente: <strong>{selectedAppointment.first_name} {selectedAppointment.last_name} {selectedAppointment.maternal_last_name || ''}</strong>
                            </p>

                            <form onSubmit={submitReschedule} className="reschedule-form">
                                <div className="form-group">
                                    <label><Calendar size={16} /> Nueva Fecha</label>
                                    <input
                                        type="date"
                                        required
                                        value={rescheduleData.appointment_date}
                                        onChange={e => setRescheduleData({ ...rescheduleData, appointment_date: e.target.value })}
                                    />
                                </div>
                                <div className="form-group">
                                    <label><Clock size={16} /> Nueva Hora</label>
                                    <input
                                        type="time"
                                        required
                                        value={rescheduleData.appointment_time}
                                        onChange={e => setRescheduleData({ ...rescheduleData, appointment_time: e.target.value })}
                                    />
                                </div>

                                <div className="modal-actions">
                                    <button type="button" className="btn-cancel" onClick={() => setShowRescheduleModal(false)}>
                                        Cancelar
                                    </button>
                                <button type="submit" className="btn-submit" disabled={reschedulingAppointment}>
                                    {reschedulingAppointment ? 'Guardando...' : 'Guardar Cambios'}
                                </button>
                                </div>
                            </form>
                        </div>
                    </div>
                </div>
            )}
        </div>
    );
};

export default SampleAppointments;

